// Utility functions for filtering that can be tested independently

// Helper function to check if a filter matches against all fields
export const matchesAllFields = (row, filter) => {
  const cmp = String(filter.value).toLowerCase();
  
  // Search through all enumerable properties of the row
  for (const [, value] of Object.entries(row)) {
    if (value == null) continue;
    const val = String(value).toLowerCase();
    
    let matches = false;
    switch (filter.op) {
      case 'equals': matches = val === cmp; break;
      case 'startsWith': matches = val.startsWith(cmp); break;
      case 'endsWith': matches = val.endsWith(cmp); break;
      case 'contains': matches = val.includes(cmp); break;
      case 'regex': 
        try {
          const regex = new RegExp(filter.value, 'i'); // case insensitive
          matches = regex.test(String(value));
        } catch (e) {
          matches = false; // Invalid regex
        }
        break;
      default: break;
    }
    
    if (matches) return true; // Found a match in any field
  }
  
  return false; // No match found in any field
};

// Helper function to check if a single filter matches a value
export const matchesFilter = (value, filter) => {
  if (value == null) return false;
  const val = String(value).toLowerCase();
  const cmp = String(filter.value).toLowerCase();
  
  switch (filter.op) {
    case 'equals': return val === cmp;
    case 'startsWith': return val.startsWith(cmp);
    case 'endsWith': return val.endsWith(cmp);
    case 'contains': return val.includes(cmp);
    case 'regex': 
      try {
        const regex = new RegExp(filter.value, 'i'); // case insensitive
        return regex.test(String(value));
      } catch (e) {
        return false; // Invalid regex
      }
    default: return false;
  }
};

// Main filtering logic with OR logic for same-column filters
export const rowMatchesFilters = (row, filters) => {
  if (!filters || !filters.length) return true;
  
  // Separate include and exclude filters
  const includeFilters = filters.filter(f => f.mode !== 'exclude');
  const excludeFilters = filters.filter(f => f.mode === 'exclude');
  
  // Check exclude filters first (faster to exclude)
  // Group exclude filters by column for OR logic within same column
  const excludeFiltersByColumn = excludeFilters.reduce((acc, f) => {
    if (!acc[f.column]) acc[f.column] = [];
    acc[f.column].push(f);
    return acc;
  }, {});
  
  for (const [, columnFilters] of Object.entries(excludeFiltersByColumn)) {
    // OR logic within the same column - if any filter matches, exclude the row
    const columnMatches = columnFilters.some(f => {
      if (f.column === '__all_fields__') {
        return matchesAllFields(row, f);
      } else {
        return matchesFilter(row[f.column], f);
      }
    });
    
    if (columnMatches) return false; // Exclude this row
  }
  
  // Check include filters
  // Group include filters by column for OR logic within same column, AND logic between columns
  const includeFiltersByColumn = includeFilters.reduce((acc, f) => {
    if (!acc[f.column]) acc[f.column] = [];
    acc[f.column].push(f);
    return acc;
  }, {});
  
  // Each column group must have at least one matching filter (AND between columns)
  for (const [, columnFilters] of Object.entries(includeFiltersByColumn)) {
    // OR logic within the same column - at least one filter must match
    const columnMatches = columnFilters.some(f => {
      if (f.column === '__all_fields__') {
        return matchesAllFields(row, f);
      } else {
        return matchesFilter(row[f.column], f);
      }
    });
    
    if (!columnMatches) return false; // Column requirement not met
  }
  
  return true;
};
